import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import { userRoles } from '../../data/mockData'
import { useProjects } from '../../hooks/useProjects.jsx'
import { useTranslation } from 'react-i18next'

function BudgetTracking() {
  const { t } = useTranslation('money')
  const { t: tc } = useTranslation('common')
  const navigate = useNavigate()
  const { user } = useAuth()
  const { projects: allProjects, loading, refresh } = useProjects()
  const [selectedProject, setSelectedProject] = useState('all')
  const [timeframe, setTimeframe] = useState('current')

  const canViewBudgets = user?.role === userRoles.ADMIN ||
    user?.role === userRoles.FINANCE ||
    user?.role === userRoles.Funder ||
    user?.role === userRoles.PROJECT_MANAGER

  // useProjects() already filters by role server-side (PM sees own only, etc.)
  // Normalise field names so downstream code can use camelCase consistently.
  const projects = (allProjects || []).map(p => {
    const approvedBudget = p.approvedBudget || p.approved_budget || p.total_budget || 0
    const spentBudget = p.spentBudget || p.spent_budget || 0
    return {
      ...p,
      approvedBudget,
      spentBudget,
      totalBudget: p.totalBudget || p.total_budget || 0,
      remainingBudget: approvedBudget - spentBudget,
      projectManagerId: p.projectManagerId || p.project_manager_id,
    }
  })

  const getUtilizationColor = (rate) => {
    const numRate = parseFloat(rate)
    if (numRate >= 90) return 'text-red-600 bg-red-100'
    if (numRate >= 75) return 'text-yellow-600 bg-yellow-100'
    return 'text-green-600 bg-green-100'
  }

  const getBudgetStatus = (spent, allocated) => {
    if (!allocated || allocated <= 0) return { status: 'No budget', color: 'bg-gray-400' }
    const utilization = (spent / allocated) * 100
    if (utilization >= 95) return { status: 'Critical', color: 'bg-red-500' }
    if (utilization >= 80) return { status: 'Warning', color: 'bg-yellow-500' }
    if (utilization >= 60) return { status: 'Good', color: 'bg-green-500' }
    return { status: 'Low Usage', color: 'bg-blue-500' }
  }

  const totalAllocated = projects.reduce((sum, p) => sum + (p.approvedBudget || 0), 0)
  const totalSpent = projects.reduce((sum, p) => sum + (p.spentBudget || 0), 0)
  const totalRemaining = totalAllocated - totalSpent

  // Real category breakdown — group projects by their category
  const categoryMap = {}
  projects.forEach(p => {
    const key = p.category || 'General'
    if (!categoryMap[key]) categoryMap[key] = { category: key, spent: 0, allocated: 0 }
    categoryMap[key].spent += (p.spentBudget || 0)
    categoryMap[key].allocated += (p.approvedBudget || 0)
  })
  const categoryColors = ['bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-orange-500', 'bg-pink-500']
  const categoryBreakdown = Object.values(categoryMap).map((c, i) => ({ ...c, color: categoryColors[i % categoryColors.length] }))

  if (!canViewBudgets) {
    return (
      <div className="p-6">
        <div className="card">
          <div className="p-12 text-center">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2-2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            <h3 className="mt-4 text-lg font-medium text-gray-900">{t('access_restricted')}</h3>
            <p className="mt-2 text-gray-600">{t('no_perm_budget')}</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      {/* header */}
      <div className="bg-linear-to-r from-green-600 to-blue-600 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">{t('budget_tracking')}</h1>
            <p className="mt-1 opacity-90">{t('monitor_budget')}</p>
          </div>
          <div className="text-right">
            <p className="text-sm opacity-90">{t('overall_utilization')}</p>
            <p className="text-3xl font-bold">{Math.round((totalSpent / totalAllocated) * 100)}%</p>
            <p className="text-sm opacity-90">${totalSpent.toLocaleString()} / ${totalAllocated.toLocaleString()}</p>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="card">
        <div className="p-4">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0 gap-4">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4 w-full lg:w-auto">
              {/* Project Filter */}
              <div className="w-full sm:w-auto">
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('project_filter')}</label>
                <div className="relative">
                  <select
                    value={selectedProject}
                    onChange={(e) => setSelectedProject(e.target.value)}
                    className="form-input w-full sm:w-64"
                  >
                    <option value="all">{t('all_projects')}</option>
                    {projects.map(project => (
                      <option key={project.id} value={project.id}>{project.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Timeframe Filter */}
              <div className="w-full sm:w-auto">
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('timeframe')}</label>
                <select
                  value={timeframe}
                  onChange={(e) => setTimeframe(e.target.value)}
                  className="form-input w-full sm:w-40"
                >
                  <option value="current">{t('current_month')}</option>
                  <option value="quarter">{t('this_quarter')}</option>
                  <option value="year">{t('this_year')}</option>
                </select>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-2 w-full lg:w-auto">
              <button onClick={() => window.print()} className="btn-secondary w-full sm:w-auto flex items-center justify-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3M3 17V7a2 2 0 012-2h14a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                </svg>
                Print / Export
              </button>
              <button onClick={() => refresh && refresh()} className="btn-primary w-full sm:w-auto flex items-center justify-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Refresh
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="card p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-blue-100">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">{t('total_allocated')}</p>
              <p className="text-2xl font-bold text-gray-900">${totalAllocated.toLocaleString()}</p>
              <p className="text-xs text-gray-500">Across {projects.length} project{projects.length !== 1 ? 's' : ''}</p>
            </div>
          </div>
        </div>
        <div className="card p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-red-100">
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">{t('total_spent')}</p>
              <p className="text-2xl font-bold text-gray-900">${totalSpent.toLocaleString()}</p>
              <p className="text-xs text-gray-500">{t('paid_to_date')}</p>
            </div>
          </div>
        </div>
        <div className="card p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-green-100">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">{t('remaining')}</p>
              <p className="text-2xl font-bold text-gray-900">${totalRemaining.toLocaleString()}</p>
              <p className="text-xs text-gray-500">{Math.round((totalRemaining / totalAllocated) * 100)}% available</p>
            </div>
          </div>
        </div>
        <div className="card p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-purple-100">
              <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">{t('active_projects')}</p>
              <p className="text-2xl font-bold text-gray-900">{projects.filter(p => p.status === 'In Progress').length}</p>
              <p className="text-xs text-blue-600">{t('currently_funded')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Project Budget details */}
      <div className="card">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">{t('project_budget_details')}</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{tc('labels.name')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('approved_budget')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('spent')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('remaining')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('utilization')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{tc('labels.status')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{tc('labels.actions')}</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {projects.map((project) => {
                const utilizationRate = Math.round((project.spentBudget / project.approvedBudget) * 100)
                const budgetStatus = getBudgetStatus(project.spentBudget, project.approvedBudget)

                return (
                  <tr key={project.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{project.name}</div>
                        <div className="text-sm text-gray-500">PM: {project.projectManager}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ${project.approvedBudget.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ${project.spentBudget.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ${project.remainingBudget.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-16 bg-gray-200 rounded-full h-2 mr-3">
                          <div
                            className={`h-2 rounded-full ${utilizationRate >= 90 ? 'bg-red-500' :
                              utilizationRate >= 75 ? 'bg-yellow-500' :
                                'bg-green-500'
                              }`}
                            style={{ width: `${Math.min(utilizationRate, 100)}%` }}
                          ></div>
                        </div>
                        <span className={`text-sm font-medium px-2 py-1 rounded-full ${getUtilizationColor(utilizationRate + '%')}`}>
                          {utilizationRate}%
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium text-white ${budgetStatus.color}`}>
                        {budgetStatus.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex space-x-2 justify-end">
                        <button
                          onClick={() => project.id && navigate(`/projects/${project.id}`)}
                          className="text-blue-600 hover:text-blue-900"
                        >{t('view_details')}</button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Budget Alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">{t('budget_alerts')}</h3>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {projects
                .filter(p => (p.spentBudget / p.approvedBudget) * 100 >= 80)
                .map(project => (
                  <div key={project.id} className="flex items-start p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div className="shrink-0">
                      <svg className="h-5 w-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="ml-3 flex-1">
                      <p className="text-sm font-medium text-red-800">{project.name}</p>
                      <p className="text-sm text-red-700">
                        High budget utilization: {Math.round((project.spentBudget / project.approvedBudget) * 100)}%
                      </p>
                      <p className="text-xs text-red-600 mt-1">
                        Remaining: ${project.remainingBudget.toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))}

              {projects.filter(p => (p.spentBudget / p.approvedBudget) * 100 >= 80).length === 0 && (
                <div className="text-center py-8">
                  <svg className="mx-auto h-8 w-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="mt-2 text-sm text-green-700">{t('no_budget_alerts')}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="card">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">{t('budget_performance')}</h3>
          </div>
          <div className="p-6 space-y-6">
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-3">{t('category_breakdown')}</h4>
              {categoryBreakdown.length === 0 ? (
                <p className="text-sm text-gray-400 py-2">{t('no_categories')}</p>
              ) : (
                <div className="space-y-3">
                  {categoryBreakdown.map((item, index) => {
                    const utilization = item.allocated > 0 ? (item.spent / item.allocated) * 100 : 0
                    return (
                      <div key={index}>
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-sm font-medium text-gray-700">{item.category}</span>
                          <span className="text-sm text-gray-600">
                            ${item.spent.toLocaleString()} / ${item.allocated.toLocaleString()}
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full ${item.color}`}
                            style={{ width: `${Math.min(utilization, 100)}%` }}
                          ></div>
                        </div>
                        <div className="mt-1 text-xs text-gray-500">
                          {Math.round(utilization)}% utilized
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default BudgetTracking
